import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatDate,
  formatText,
  theme,
  Tooltip,
  DeprecatedChevronUpIcon,
  DeprecatedInfoIcon,
  DeprecatedChevronDownIcon,
} from '@arnold/common';
import {
  getQuestionDefinitionByIndex,
  getQuestionType,
  isScaleEscapeValue,
  parseScaleValueIndex,
} from '../../lib/reportHelper';
import { Features, QuestionType, Reaction, ReportSequenceRespondentDetailQueryQuery } from '../../generated/hooks';
import { IReport } from '../../lib/reports/base';
import { HighlightedAnswersIcon } from './HighlightedAnswerIcon';
import { Answer, IMessage } from './RespondentsAnswers';
import { AnswerType } from '../../lib/reports/question';
import styled from '@emotion/styled';
import { ReactionModal } from './ReactionModal';
import { useUser } from '../../components/providers/UserProvider';
import auth from '../../lib/auth';
import { shortQuestion } from '@arnold/core';

type ConversationMessagesProps = {
  messages: IMessage[];
  report: IReport | ReportSequenceRespondentDetailQueryQuery['getProcessReportRespondentDetail'];
  reload?: () => void;
};

const ConversationMessages = ({ messages, report, reload }: ConversationMessagesProps) => {
  const { t } = useTranslation('Report');
  const { user } = useUser();
  const [expandedMessageIds, setExpandedMessageIds] = React.useState<Set<string>>(new Set());
  const [showReactionModalMessageId, setShowReactionModalMessageId] = useState<string | null>(null);

  const accessToken = report.identifier;
  const allResponses = report.responses || [];

  // Check if current user has admin privileges for enhanced features
  const isManagersReportOrLoggedIn = !!user?.user || !!report.metadata?.respondent;
  if (!messages) return null;

  const renderedNodes: React.ReactNode[] = [];
  let scaleGroupBuffer: React.ReactNode[] = [];
  let currentScaleQuestionIndex: string | null = null;
  let currentScaleQuestionText: string = '';

  const flushScaleGroup = () => {
    if (scaleGroupBuffer.length > 0) {
      renderedNodes.push(
        <div key={`scale-group-${renderedNodes.length}`}>
          <div
            className={`mb-6`}
            style={{
              fontSize: theme.typography.body.medium.regular.fontSize,
              color: theme.colors.text.secondary,
              maxWidth: '500px',
            }}
            title={currentScaleQuestionText}
          >
            {formatText(currentScaleQuestionText)}
          </div>
          <div
            style={{
              borderLeft: `2px solid ${theme.colors.borderSeparator.default}`,
              paddingLeft: theme.spacing.g,
              marginBottom: theme.spacing.f,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {scaleGroupBuffer}
          </div>
        </div>,
      );
      scaleGroupBuffer = [];
      currentScaleQuestionIndex = null;
      currentScaleQuestionText = '';
    }
  };

  messages.forEach((message) => {
    const questionDefinition = getQuestionDefinitionByIndex(report, message.questionDefinitionIndex);
    const originalMessage = allResponses
      .flatMap((response) => response.messages || [])
      .find((responseMessage: any) => responseMessage.id === message.id);

    let questionLabel = message.question;
    if (
      questionDefinition &&
      questionDefinition.type === QuestionType.Scale &&
      originalMessage &&
      questionDefinition.statementsLocal
    ) {
      const statementIndexNumber =
        originalMessage.statementIndex != null ? Number(originalMessage.statementIndex) : NaN;
      if (!Number.isNaN(statementIndexNumber) && questionDefinition.statementsLocal[statementIndexNumber]) {
        questionLabel = shortQuestion(questionDefinition.statementsLocal[statementIndexNumber] ?? '');
      }
    }

    let selectedOptionLabel: React.ReactNode = message.answers.map((answer) => answer.text).join(', ');
    let answerHighlightType: AnswerType | null = null;
    let selectedScaleIndex: number = -1;
    const showEscapeOption = questionDefinition?.showEscapeOption ?? true;
    if (questionDefinition && questionDefinition.type === QuestionType.Scale && originalMessage) {
      const parsedValue = parseScaleValueIndex(originalMessage.value);
      selectedScaleIndex = parsedValue == null ? -1 : parsedValue;
      if (
        selectedScaleIndex >= 0 &&
        questionDefinition.answersLocal &&
        questionDefinition.answersLocal[selectedScaleIndex] != null
      ) {
        selectedOptionLabel = String(questionDefinition.answersLocal[selectedScaleIndex]);
      } else if (showEscapeOption && isScaleEscapeValue(originalMessage.value)) {
        selectedOptionLabel = showEscapeOption ? (
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <span>{t('bubble:scaleEscapeOption')}</span>
            <span className={'ml-2'}>
              <Tooltip title={t('scaleEscapeOptionTooltip', { scaleEscapeOption: t('bubble:scaleEscapeOption') })}>
                <DeprecatedInfoIcon />
              </Tooltip>
            </span>
          </span>
        ) : (
          ''
        );
      } else {
        selectedOptionLabel = '';
      }
      const highlightLevel = (questionDefinition.answersToHighlight || []).reduce((accumulator, indexOrString) => {
        const optionIndex =
          typeof indexOrString === 'string' ? parseInt(indexOrString as string, 10) : Number(indexOrString);
        return accumulator + (optionIndex === selectedScaleIndex ? 1 : 0);
      }, 0);
      if (highlightLevel >= 2) {
        answerHighlightType = AnswerType.ALERT;
      } else if (highlightLevel === 1) {
        answerHighlightType = AnswerType.WARNING;
      }
    }

    if (!answerHighlightType && questionDefinition) {
      const normalized = getQuestionType(questionDefinition);
      if (normalized === QuestionType.Select || normalized === QuestionType.Multiselect) {
        const hasAlert = message.answers.some((ans) => ans.type === AnswerType.ALERT);
        const hasWarning = message.answers.some((ans) => ans.type === AnswerType.WARNING);
        if (hasAlert) {
          answerHighlightType = AnswerType.ALERT;
        } else if (hasWarning) {
          answerHighlightType = AnswerType.WARNING;
        }
      }
    }

    const commentText = originalMessage?.comment || '';
    const normalizedType = questionDefinition ? getQuestionType(questionDefinition as any) : null;
    const isScaleMessage = !!(questionDefinition && normalizedType === QuestionType.Scale);
    const isEligibleType =
      normalizedType === QuestionType.Select ||
      normalizedType === QuestionType.Multiselect ||
      normalizedType === QuestionType.Scale;

    const isExpanded = expandedMessageIds.has(message.id);
    const toggleExpanded = () => {
      setExpandedMessageIds((prev) => {
        const next = new Set(prev);
        if (next.has(message.id)) {
          next.delete(message.id);
        } else {
          next.add(message.id);
        }
        return next;
      });
    };
    const showReactionButton =
      (isEligibleType || (originalMessage?.value?.trim()?.length ?? 0) > 0) &&
      report.metadata?.organization.featureNames?.includes(Features.ReportReactionToMessage);
    const messageNode = (
      <div key={message.id} className={`mb-6`} data-cy="report-answers-item" id={message.id}>
        <div
          className={`mb-4`}
          style={{
            fontSize: theme.typography.body.medium.regular.fontSize,
            color: theme.colors.text.secondary,
          }}
          title={questionLabel}
        >
          {formatText(questionLabel)}
        </div>
        <AnswerWrapper isScale={isScaleMessage}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.d }}>
            <div style={{ display: 'flex', flexDirection: 'row', gap: theme.spacing.c }}>
              <Answer noMaxHeight className={`mb-0 rounded-lg`} style={{ width: '100%' }}>
                <span className={'d-flex align-items-center w-100'} style={{ justifyContent: 'space-between' }}>
                  <span className={'d-flex align-items-center'} style={{ gap: theme.spacing.c, flex: 1 }}>
                    {answerHighlightType && <HighlightedAnswersIcon type={answerHighlightType} />}
                    {selectedOptionLabel}
                  </span>
                </span>
              </Answer>
              {isEligibleType && (
                <ToggleBox
                  type="button"
                  aria-expanded={isExpanded}
                  aria-label="Show all options"
                  onClick={toggleExpanded}
                  data-icom="report-answer-show-all-options"
                >
                  {isExpanded ? <DeprecatedChevronUpIcon /> : <DeprecatedChevronDownIcon />}
                </ToggleBox>
              )}
            </div>
            {isEligibleType && isExpanded && questionDefinition?.answersLocal && (
              <OptionsWrapper>
                <OptionsBox className={`rounded-lg`}>
                  <div className={'d-flex align-items-center'}>{t('allChoicesLabel')}</div>
                  {(questionDefinition.answersLocal || []).map((option, idx) => {
                    const isSelected =
                      (normalizedType === QuestionType.Scale && idx === selectedScaleIndex) ||
                      (normalizedType !== QuestionType.Scale &&
                        message.answers.some((answer) => answer.text === String(option)));

                    return (
                      <div
                        key={`opt-${message.id}-${idx}`}
                        className={`d-flex align-items-center ${isSelected ? 'selectedOptionDiv' : ''}`}
                      >
                        <span>
                          {option === '' && showEscapeOption ? t('bubble:scaleEscapeOption') : String(option)}
                        </span>
                      </div>
                    );
                  })}
                </OptionsBox>
              </OptionsWrapper>
            )}
            {commentText && commentText.trim().length > 0 && (
              <Answer noMaxHeight className={`mb-0 rounded-lg`} style={{ width: '100%' }}>
                <span>{commentText}</span>
              </Answer>
            )}
          </div>
          {showReactionButton && (
            <ReactionLabelLinkButton
              onClick={() => {
                if (isManagersReportOrLoggedIn) {
                  setShowReactionModalMessageId(message.id);
                } else {
                  auth.redirectToLogin();
                }
              }}
              data-cy="reaction-on-message-button"
              data-icom="reaction-on-message-button"
            >
              {t('reactLabel')}
            </ReactionLabelLinkButton>
          )}
        </AnswerWrapper>
        {allResponses
          .filter((r) => (r.messages || []).some((m) => m.id === message.id))
          .flatMap((r) => r.reactions || [])
          .filter((reaction) => reaction.messageId === message.id)
          .map((reaction) => (
            <AlignRight>
              <MessageReaction key={`reaction-${reaction.id}`} reaction={reaction} />
            </AlignRight>
          ))}
      </div>
    );

    if (questionDefinition && questionDefinition.type === QuestionType.Scale) {
      if (currentScaleQuestionIndex !== message.questionDefinitionIndex) {
        flushScaleGroup();
        currentScaleQuestionIndex = message.questionDefinitionIndex;
        currentScaleQuestionText = questionDefinition?.text || '';
      }
      scaleGroupBuffer.push(messageNode);
    } else {
      flushScaleGroup();
      renderedNodes.push(messageNode);
    }
  });

  flushScaleGroup();
  return (
    <>
      {renderedNodes}
      {showReactionModalMessageId && (
        <ReactionModal
          accessToken={accessToken}
          channelId={
            allResponses.find((r) =>
              showReactionModalMessageId
                ? (r.messages || []).some((m: any) => m.id === showReactionModalMessageId)
                : true,
            )!.id
          }
          messageId={showReactionModalMessageId}
          onClose={() => setShowReactionModalMessageId(null)}
          reload={reload}
          dataCy="reaction-modal"
        />
      )}
    </>
  );
};

export const MessageReaction = ({ reaction }: { reaction: Reaction }) => {
  return (
    <ReactionWrapper data-cy="reaction-wrapper">
      <ReactionName>{reaction.respondentFullName}</ReactionName>
      <ReactionText data-cy="reaction-text">{reaction.text}</ReactionText>
      <ReactionDate>{formatDate(reaction.createdAt)}</ReactionDate>
    </ReactionWrapper>
  );
};

const ReactionWrapper = styled.div`
  margin-top: ${theme.spacing.f};
  max-width: 456px;
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: ${theme.spacing.d};
`;

const ReactionName = styled.span`
  font-size: ${theme.typography.body.medium.regular.fontSize};
  color: ${theme.colors.text.secondary};
`;

const ReactionText = styled.span`
  padding: ${theme.spacing.d} ${theme.spacing.f};
  background-color: ${theme.colors.backgroundBasic.default};
  border-radius: ${theme.borderRadius.m};
  font-size: ${theme.typography.body.medium.regular.fontSize};
  color: ${theme.colors.text.primary};
`;

const ReactionDate = styled.span`
  margin-left: auto;
  font-size: ${theme.typography.body.small.regular.fontSize};
  color: ${theme.colors.text.disabled};
`;

const AlignRight = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const AnswerWrapper = styled.div<{ isScale: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.d};
  max-width: 480px;
  width: 100%;
`;

const ToggleBox = styled.button`
  width: 38px;
  height: 38px;
  padding: ${theme.spacing.d};
  border-radius: ${theme.borderRadius.m};
  background: ${theme.colors.backgroundBasic.default};
  cursor: pointer;
  border: none;

  svg * {
    fill: ${theme.colors.iconText.primary} !important;
  }
`;

const OptionsWrapper = styled.div`
  padding-left: ${theme.spacing.g};
`;

const OptionsBox = styled.div`
  border-radius: ${theme.borderRadius.m};
  background: ${theme.colors.backgroundBasic.default};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.c};
  padding: ${theme.spacing.d} ${theme.spacing.f};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.body.medium.regular.fontSize};
  font-weight: ${theme.typography.body.medium.regular.fontWeight};
  line-height: ${theme.typography.body.medium.regular.lineHeight};

  & > .selectedOptionDiv {
    color: ${theme.colors.text.primary};
    font-weight: ${theme.typography.body.medium.bold.fontWeight};
  }
`;

const ReactionLabelLinkButton = styled.span`
  cursor: pointer;
  display: flex;
  justify-content: flex-end;
  width: fit-content;
  align-self: end;
  color: ${theme.colors.actionPrimary.default};
  font-weight: ${theme.typography.body.medium.link.fontWeight};
  font-size: ${theme.typography.body.medium.link.fontSize};
  line-height: ${theme.typography.body.medium.link.lineHeight};
`;

export { ConversationMessages };
